import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { Form } from "@/components/ui/form";
import { z } from "zod";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import FormInputField from "@/components/lib/from-input-field";
import useAuth from "@/hooks/useAuth";
import {
  createNewCategory,
  clearCategoryMessages,
} from "@/redux/slice/essential-menu-slice/category-slice";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export default function CreateCategoryDialog({
  isOpen,
  onClose,
  title,
  menu_id,
}) {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [showIsChaild, setShowIsChaild] = useState(false);
  const { createLoading, error, categories } = useSelector(
    (state) => state.essentialMenu.categories
  );

  const form = useForm({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      order: "",
      parent_id: null,
      status: "active",
    },
  });

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      menu_item_id: menu_id,
      client_id: user?.client_id,
      restaurant_id: user?.restaurant_id,
    };
    try {
      dispatch(clearCategoryMessages());
      const res = await dispatch(createNewCategory(payload));
      if (createNewCategory.fulfilled.match(res)) {
        toast.success("Category created successfull.");
        form.reset();
        dispatch(clearCategoryMessages());
        onClose();
      } else if (createNewCategory.rejected.match(res)) {
        toast.error(res.payload || "Failed to create category.");
      }
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong.");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <div className="py-5 flex flex-col">
          <DialogTitle className="text-lg absolute top-4 font-medium">
            {title || "Create New"}
          </DialogTitle>
          <Separator className="mt-4" />

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-6 mt-5"
            >
              <FormInputField
                name={"name"}
                style="bg-cblack3"
                placeholder={"Name"}
                form={form}
                label={"Name"}
                type="text"
              />
              <div className="space-y-4">
                <div className="flex gap-2 items-center">
                  <Switch
                    className="data-[state=unchecked]:bg-neutral-500"
                    checked={showIsChaild}
                    onCheckedChange={(val) => {
                      setShowIsChaild(val);
                      if (!val) {
                        form.setValue("parent_id", null);
                      }
                    }}
                  />
                  <Label>Is Child</Label>
                </div>
                {showIsChaild && (
                  <FormInputField
                    name="parent_id"
                    style="bg-cblack3 flex-1"
                    placeholder="Select Category"
                    form={form}
                    type="select"
                    search={true}
                    parseAs="number"
                    selectOption={
                      categories?.length
                        ? categories.filter((item) => item?.status === "active").map((item) => ({
                            label: item?.name,
                            value: item?.id,
                          }))
                        : [
                            {
                              label: "No Menu available",
                              value: "",
                              disabled: true,
                            },
                          ]
                    }
                    label="Parent Category"
                  />
                )}
              </div>

              <FormInputField
                name="order"
                style="bg-cblack3"
                placeholder="0"
                form={form}
                type="text"
                label="View Order"
              />
              <FormInputField
                name="description"
                style="bg-cblack3"
                placeholder={"Description"}
                form={form}
                label={"Description "}
                type="textarea"
              />
              <Button
                isLoading={createLoading}
                type="submit"
                className="bg-cyellow w-full text-cblack"
              >
                Confirm
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  parent_id: z.number().nullable().optional(),
  order: z
    .union([z.string(), z.number()])
    .transform((val) => (val === "" ? undefined : Number(val)))
    .refine((val) => val === undefined || Number.isInteger(val), {
      message: "View order must be an integer",
    })
    .optional(),
  status: z.enum(["active", "archive", "draft"]).default("active"),
});
